import React, {forwardRef, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {ClickEventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isInteger} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  NumberInputBox,
  NumberInputBoxProps,
} from '../../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';

/*
 * Props.
 */

interface OrderInputBox extends NumberInputBoxProps {
  /** @default onChange(value + 1) */
  onDownArrow?: ClickEventHandler;
  /** @default onChange(value - 1) */
  onUpArrow?: ClickEventHandler;

  disableDownArrow?: boolean;
  disableUpArrow?: boolean;

  /** @default "Move order up" */
  downArrowLabel?: ReactNode;
  /** @default "Move order down" */
  upArrowLabel?: ReactNode;

  inputWidth?: number | string;

  hasSmallArrows?: boolean;
}

/*
 * Styles.
 */

const StyledArrowButton = styled(IconButton)<{
  $gridArea: 'arrow-up' | 'arrow-down';
  $hasSmallArrows: boolean;
}>`
  grid-area: ${(p) => p.$gridArea};
  ${(p) => p.$hasSmallArrows && 'padding: 3px;'}
  width: ${(p) => (p.$hasSmallArrows ? 30 : 26)}px;

  &[aria-disabled='true'] {
    background-color: transparent;
    opacity: 0.4;
  }
`;

const StyledNumberInputBox = styled(NumberInputBox)<{$inputWidth: number | string | undefined}>`
  grid-area: input;
  align-self: center;
  width: ${(p) =>
    p.$inputWidth && (typeof p.$inputWidth === 'number' ? `${p.$inputWidth}px` : p.$inputWidth)};
`;

const StyledOrderInputWrapper = styled.div`
  display: grid;
  grid-template-areas:
    'input arrow-up'
    'input arrow-down';

  grid-gap: 0 6px;

  > :last-child {
    margin-top: -4px;
  }
`;

/*
 * Component.
 */

export const OrderInputBox = forwardRef<BoxedInput, OrderInputBox>(
  (
    {
      onDownArrow,
      onUpArrow,
      disableDownArrow,
      disableUpArrow,
      downArrowLabel,
      upArrowLabel,
      inputWidth,
      hasSmallArrows,
      ...inputProps
    },
    ref,
  ) => (
    <StyledOrderInputWrapper>
      <StyledNumberInputBox
        {...inputProps}
        $inputWidth={inputWidth}
        // We always disable arrows since we render custom ones.
        isArrowsDisabled
        ref={ref}
      />
      {!inputProps.isArrowsDisabled && (
        <>
          <StyledArrowButton
            $gridArea="arrow-up"
            $hasSmallArrows={Boolean(hasSmallArrows)}
            iconName="chevronUpSmall"
            label={upArrowLabel ?? <Trans id="number-input-cell-move-down">Move up</Trans>}
            onClick={
              onUpArrow ??
              ((event) => {
                event.preventDefault();
                if (isInteger(inputProps.value)) {
                  inputProps.onChange?.(inputProps.value - 1);
                }
              })
            }
            isDisabled={disableUpArrow}
          />
          <StyledArrowButton
            $gridArea="arrow-down"
            $hasSmallArrows={Boolean(hasSmallArrows)}
            iconName="chevronDownSmall"
            label={downArrowLabel ?? <Trans id="number-input-cell-move-up">Move down</Trans>}
            onClick={
              onDownArrow ??
              ((event) => {
                event.preventDefault();
                if (isInteger(inputProps.value)) {
                  inputProps.onChange?.(inputProps.value + 1);
                }
              })
            }
            isDisabled={disableDownArrow}
          />
        </>
      )}
    </StyledOrderInputWrapper>
  ),
);
